import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/feature/SEOHead";
import { articles } from "@/mocks/data";

export default function ArticlePage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 md:pt-24 container-page min-h-[60vh] flex flex-col items-center justify-center">
          <h1 className="text-2xl font-bold text-foreground-900 mb-4">文章未找到</h1>
          <button
            onClick={() => {
              navigate("/category/cross-border-business");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm cursor-pointer whitespace-nowrap"
          >
            {t("article.back_to_list")}
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    author: {
      "@type": "Person",
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      "@type": "Organization",
      name: "1996至成国际",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: t("breadcrumb.home"),
        item: "https://www.1996zhcheng.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: t("breadcrumb.articles"),
        item: "https://www.1996zhcheng.com/category/cross-border-business",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: article.title,
        item: `https://www.1996zhcheng.com/article/${article.id}`,
      },
    ],
  };

  const breadcrumbItems = [
    { label: t("breadcrumb.home"), path: "/" },
    { label: t("breadcrumb.articles"), path: "/category/cross-border-business" },
    { label: article.title },
  ];

  return (
    <>
      <SEOHead
        title={`${article.title} - 1996至成国际`}
        description={article.excerpt}
        keywords={`${article.categoryName},1996至成国际,至成国际资讯,${article.title.slice(0, 20)}`}
        canonicalUrl={`https://www.1996zhcheng.com/article/${article.id}`}
        ogType="article"
        schemaJson={[articleSchema, breadcrumbSchema]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-20 md:pt-24">
          <div className="container-page">
            <Breadcrumb items={breadcrumbItems} />

            <article className="max-w-4xl mx-auto py-8 md:py-12">
              {/* Article Header */}
              <header className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-accent-50 text-accent-600">
                    {article.categoryName}
                  </span>
                  <span className="text-xs text-foreground-400">{article.publishDate}</span>
                </div>
                <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-500">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-user-3-line"></i>
                    <span>{article.author}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-eye-line"></i>
                    <span>{article.readCount.toLocaleString()} 阅读</span>
                  </div>
                </div>
              </header>

              {/* Featured Image */}
              <div className="w-full h-64 md:h-96 rounded-xl overflow-hidden mb-10">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover"
                  title={article.title}
                />
              </div>

              {/* Article Content */}
              <div className="prose-custom text-foreground-800 leading-relaxed">
                {article.content.split("\n\n").map((paragraph, idx) => (
                  <p key={idx} className="mb-5 text-base leading-loose text-foreground-700">
                    {paragraph.trim()}
                  </p>
                ))}
              </div>

              {/* Article Footer */}
              <div className="mt-10 pt-8 border-t border-background-200/70">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-semibold text-sm">
                      {article.author[0]}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{article.author}</div>
                      <div className="text-xs text-foreground-500">至成国际专栏作者</div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      navigate("/category/cross-border-business");
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className="px-5 py-2.5 rounded-full border border-foreground-300 text-foreground-600 text-sm font-medium hover:border-primary-500 hover:text-primary-600 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1"
                  >
                    <i className="ri-arrow-left-line"></i>
                    {t("article.back_to_list")}
                  </button>
                </div>
              </div>
            </article>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="max-w-4xl mx-auto pb-16">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-6">
                  {t("article.related")}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                      className="group block p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 no-underline"
                    >
                      <div className="w-full h-40 rounded-lg overflow-hidden mb-3">
                        <img
                          src={ra.imageUrl}
                          alt={ra.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          title={ra.title}
                        />
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-2">{ra.publishDate}</p>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}